import { msg } from "@lingui/core/macro";
import { createEffect, createMemo, createSignal, For, Show } from "solid-js";
import { useQuery } from "@tanstack/solid-query";
import { useSearch } from "@tanstack/solid-router";
import { Badge, Card, CardContent, CardHeader, CardTitle, Skeleton } from "@fleet/ui";
import { Button, buttonVariants } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { useI18n } from "@/providers/i18n-provider";
import { api, SERVER_BASE_URL } from "@/lib/api";
import { queryClient } from "@/lib/query-client";
import { handleApiResponse, queryKeys, sharedApi } from "@boveda/shared";
import { showToast } from "@/components/ui/toast";
import { Link2, ShieldCheck } from "@fleet/icons";
import { QueryErrorState } from "@/components/ui/query-error-state";

type ProviderId = "google" | "apple";

const PROVIDER_LABELS: Record<ProviderId, string> = {
  google: "Google",
  apple: "Apple",
};

/**
 * "Connected accounts": lets you link/unlink Google and Apple from the profile.
 * It only shows providers ENABLED in the backend. It respects the last-method
 * safeguard (the backend rejects leaving the user without access).
 */
export function ConnectedAccountsSection() {
  const { t } = useI18n();
  // Read ?linked=<provider> to show feedback after returning from the callback.
  const search = useSearch({ strict: false }) as () => { linked?: string };
  const [confirmUnlink, setConfirmUnlink] = createSignal<ProviderId | null>(null);
  const [busy, setBusy] = createSignal(false);

  // P23/I3 · BROWSER-MEASURED (phase 2, 2026-08-01): these two `queryFn`
  // were hand-written on `openapi-fetch`, which NEVER throws — it returns
  // `{data, error}`. With `return data ?? {…}` a 500 resolved in SUCCESS with a
  // FABRICATED EMPTY object, so `isError` was never set: the
  // `QueryErrorState` below was DEAD CODE (passed `tsc`, seen in
  // diff, never rendered) and the I2 `isSuccess` guard was also
  // of nothing, because status WAS `success`.
  //
  // Baseline measurement with `login-methods` on sustained 500: card
  // displayed "Google · Not connected", "Connect" button, and "This is your only
  // sign-in method" — of an account whose real state NOBODY knew. In a
  // secret manager that is precisely the information user uses to
  // decide if their access is safe.
  //
  // `handleApiResponse` THROWS on `{error}` and on an empty body — which is
  // the same thing `createApiQuery` does, the pattern used by the other error
  // error state of this repo, making the branch reachable.
  //
  // Deliberate EXCEPTION: the session `queryFn` (`lib/auth.ts`) keeps
  // SWALLOWING the error on purpose — supports guest mode. Not converted.
  const providersQuery = useQuery(() => ({
    queryKey: queryKeys.auth.socialProviders(),
    queryFn: async () => handleApiResponse(sharedApi.socialProviders(api)),
    staleTime: 5 * 60 * 1000,
    retry: false,
  }));

  const methodsQuery = useQuery(() => ({
    queryKey: queryKeys.auth.loginMethods(),
    queryFn: async () => handleApiResponse(sharedApi.loginMethods(api)),
    retry: false,
  }));

  const hasLoadFailure = () => providersQuery.isError || methodsQuery.isError;

  const enabledProviders = createMemo<ProviderId[]>(() => {
    // P23 / CC-60 · I2: `createMemo` is EAGER — evaluated at MOUNT, before
    // any `<Show>`. Reading `.data` without `isSuccess` suspends entire
    // section against route `<Suspense>`.
    if (!providersQuery.isSuccess) {
      return [];
    }
    const p = providersQuery.data;
    if (!p) {
      return [];
    }
    const list: ProviderId[] = [];
    if (p.google) {
      list.push("google");
    }
    if (p.apple) {
      list.push("apple");
    }
    return list;
  });

  // P23 / CC-60 · I2 + I3. NOTE: there was a comment claiming this
  // `isSuccess` guard prevented false "Not connected" with
  // down `login-methods`. IT DID NOT, and measured: with prior `queryFn`
  // status WAS `success` (empty object fabricated by `??`), so guard
  // let it through and screen lied. A fix written against wrong state.
  // What closes it is having `queryFn` THROW (above);
  // this guard only covers I2 case (in-flight request → do not suspend).
  const linkedProviders = createMemo(() =>
    methodsQuery.isSuccess ? methodsQuery.data.providers : [],
  );
  const hasPassword = createMemo(() =>
    methodsQuery.isSuccess ? methodsQuery.data.has_password : false,
  );

  const isLinked = (provider: ProviderId) => linkedProviders().includes(provider);

  // Total number of access methods (password + each linked social account).
  const totalMethods = createMemo(() => linkedProviders().length);

  // Is this the LAST method? (it cannot be unlinked without establishing another).
  const isLastMethod = (provider: ProviderId) =>
    isLinked(provider) && totalMethods() <= 1 && !hasPassword();

  const linkUrl = (provider: ProviderId) =>
    `${SERVER_BASE_URL.replace(/\/$/, "")}/api/auth/link-social/${provider}`;

  const handleUnlink = async (provider: ProviderId) => {
    setBusy(true);
    try {
      const { error } = await sharedApi.unlinkAccount(api, provider);
      if (error) {
        const message = error.message ?? "";
        if (message.includes("only sign-in method")) {
          showToast(t(msg`Set a password before unlinking your only sign-in method.`), "error");
        } else {
          showToast(t(msg`Could not unlink the account. Please try again.`), "error");
        }
        return;
      }
      showToast(t(msg`Account unlinked.`), "success");
      await queryClient.invalidateQueries({ queryKey: queryKeys.auth.loginMethods() });
    } finally {
      setBusy(false);
      setConfirmUnlink(null);
    }
  };

  // Feedback when returning from the linking flow (?linked=<provider>).
  let linkedFeedbackDone = false;
  createEffect(() => {
    const provider = search().linked;
    if (provider && !linkedFeedbackDone) {
      linkedFeedbackDone = true;
      showToast(t(msg`Account connected.`), "success");
      void queryClient.invalidateQueries({ queryKey: queryKeys.auth.loginMethods() });
    }
  });

  // P23/I3 · LESSON PAID: section is also shown when loading
  // FAILED. If only shown with `enabledProviders().length > 0`, a failure in
  // `social/providers` would make it vanish completely and error state
  // would become unreachable through another path — «failed to load» ≠ «none exist».
  return (
    <Show when={enabledProviders().length > 0 || hasLoadFailure()}>
      <Card>
        <CardHeader class="flex flex-row items-center gap-2 space-y-0">
          <ShieldCheck class="h-5 w-5 text-primary" />
          <CardTitle>{t(msg`Connected accounts`)}</CardTitle>
        </CardHeader>
        <CardContent class="space-y-3">
          {/* P23 / CC-60 · I3: «no se pudo cargar» ≠ «no hay nada». En error
              `isLoading`/`isPending` valen FALSE, así que el guard de abajo dejaba
              pasar, `data` era `undefined` y el `?? []` lo pintaba como VACÍO. Se
              ENVUELVE (no se encadena `&& !isError` en el mismo guard: su
              fallback es el esqueleto y quedarían error + esqueleto a la vez). */}
          <Show when={hasLoadFailure()}>
            <QueryErrorState
              testId="connected-accounts-error"
              compact
              onRetry={() => {
                void providersQuery.refetch();
                void methodsQuery.refetch();
              }}
            />
          </Show>
          <Show when={!hasLoadFailure()}>
            <Show
              when={!methodsQuery.isPending}
              fallback={
                <div class="space-y-3">
                  <Skeleton class="h-14 w-full rounded-xl" />
                  <Skeleton class="h-14 w-full rounded-xl" />
                </div>
              }
            >
              <For each={enabledProviders()}>
                {(provider) => (
                  <div class="flex items-center gap-4 rounded-xl border border-border bg-muted/30 p-4">
                    <div class="flex h-10 w-10 items-center justify-center rounded-full bg-muted text-muted-foreground">
                      <Link2 class="h-5 w-5" />
                    </div>
                    <div class="min-w-0 flex-1">
                      <p class="text-sm font-semibold">{PROVIDER_LABELS[provider]}</p>
                      <Show
                        when={isLinked(provider)}
                        fallback={
                          <p class="text-xs text-muted-foreground">{t(msg`Not connected`)}</p>
                        }
                      >
                        <Badge
                          variant="secondary"
                          class="mt-1 h-5 border-none bg-primary/20 px-1.5 text-[10px] font-bold uppercase tracking-wider text-primary"
                        >
                          {t(msg`Connected`)}
                        </Badge>
                      </Show>
                    </div>

                    <Show
                      when={isLinked(provider)}
                      fallback={
                        <a
                          href={linkUrl(provider)}
                          class={buttonVariants({ variant: "outline", size: "sm" })}
                          data-testid={`link-${provider}`}
                        >
                          {t(msg`Connect`)}
                        </a>
                      }
                    >
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={busy() || isLastMethod(provider)}
                        title={
                          isLastMethod(provider)
                            ? t(msg`Set a password before unlinking your only sign-in method.`)
                            : undefined
                        }
                        onClick={() => setConfirmUnlink(provider)}
                        class="text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                      >
                        {t(msg`Disconnect`)}
                      </Button>
                    </Show>
                  </div>
                )}
              </For>

              <Show when={!hasPassword() && totalMethods() <= 1}>
                <p class="text-xs text-muted-foreground">
                  {t(
                    msg`This is your only sign-in method. Set a password to be able to disconnect it.`,
                  )}
                </p>
              </Show>
            </Show>
          </Show>
        </CardContent>
      </Card>

      <ConfirmDialog
        open={confirmUnlink() !== null}
        onOpenChange={(open) => {
          if (!open) {
            setConfirmUnlink(null);
          }
        }}
        title={t(msg`Disconnect account?`)}
        description={t(
          msg`You will no longer be able to sign in with this provider. You can reconnect it anytime.`,
        )}
        confirmLabel={t(msg`Disconnect`)}
        cancelLabel={t(msg`Cancel`)}
        onConfirm={() => {
          const provider = confirmUnlink();
          if (provider) {
            void handleUnlink(provider);
          }
        }}
      />
    </Show>
  );
}
